import { useEffect, useMemo, useState } from 'react';
import { formatContactName, getGradeLabel, parseChildrenIds } from '@car-pool/shared/schoolRecords';
import SchoolAvatar from './SchoolAvatar';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function getParentMatchRank(parent, student) {
  const studentLastName = normalize(student?.l_name);
  const studentAddress = normalize(student?.street_address);
  const parentLastName = normalize(parent?.l_name);
  const parentAddress = normalize(parent?.street_address);

  let rank = 0;
  if (studentLastName && parentLastName === studentLastName) rank -= 20;
  if (studentAddress && parentAddress && parentAddress === studentAddress) rank -= 8;

  return rank;
}

function getSearchMatchRank(parent, query) {
  if (!query) return 0;

  const fullName = formatContactName(parent);
  const fields = [fullName, parent.email, parent.phone].map(normalize);

  if (fields.some((field) => field.startsWith(query))) return -10;
  if (fields.some((field) => field.includes(query))) return -5;
  return 0;
}

function hasLinkedChildren(parent) {
  return parseChildrenIds(parent?.children_ids).length > 0;
}

function getLinkedChildrenCount(parent) {
  return parseChildrenIds(parent?.children_ids).length;
}

function getContactTypeLabel(parent) {
  return normalize(parent?.type) === 'guardian' ? 'guardian' : 'parent';
}

function getReviewPayload(row) {
  return {
    ...(row?.best_payload || {}),
    ...(row?.manual_override_payload || {}),
  };
}

function getReviewRowTitle(row) {
  const payload = getReviewPayload(row);
  const name = formatContactName(payload);
  if (name) return name;
  return `Review row ${row?.source_row || row?.id || ''}`.trim();
}

function getReviewIssueText(row) {
  const issues = Array.isArray(row?.validation_issues) ? row.validation_issues : [];
  return issues
    .map((issue) => issue.message || issue.field)
    .filter(Boolean)
    .join('; ');
}

function compareParents(left, right, student, query, preferParentsWithoutChildren) {
  const leftRank = getParentMatchRank(left, student) + getSearchMatchRank(left, query);
  const rightRank = getParentMatchRank(right, student) + getSearchMatchRank(right, query);
  const rankDelta = leftRank - rightRank;
  if (rankDelta !== 0) return rankDelta;

  if (preferParentsWithoutChildren) {
    const leftHasChildren = hasLinkedChildren(left);
    const rightHasChildren = hasLinkedChildren(right);
    if (leftHasChildren !== rightHasChildren) {
      return leftHasChildren ? 1 : -1;
    }
  }

  const childCountDelta = getLinkedChildrenCount(left) - getLinkedChildrenCount(right);
  if (childCountDelta !== 0) {
    return childCountDelta;
  }

  const leftName = formatContactName(left).toLowerCase();
  const rightName = formatContactName(right).toLowerCase();
  return leftName.localeCompare(rightName);
}

export default function StudentParentMatchModal({
  student,
  parents = [],
  gradeLabelMap = {},
  reviewMatches = [],
  reviewMatchesLoading = false,
  reviewMatchesError = '',
  onClose,
  onAssign,
  onOpenReviewQueue,
}) {
  const [search, setSearch] = useState('');
  const [candidateParentId, setCandidateParentId] = useState('');
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filteredParents = useMemo(() => {
    const query = normalize(search);
    const nextParents = [...parents]
      .filter((parent) => {
        if (!query) return true;

        return [
          parent.f_name,
          parent.l_name,
          parent.email,
          parent.phone,
        ].some((field) => normalize(field).includes(query));
      });

    const hasPriorityMatch = nextParents.some((parent) => (
      getParentMatchRank(parent, student) < 0 || getSearchMatchRank(parent, query) < 0
    ));

    return nextParents.sort((left, right) => (
      compareParents(left, right, student, query, !hasPriorityMatch)
    ));
  }, [parents, search, student]);

  useEffect(() => {
    if (!filteredParents.length) {
      setCandidateParentId('');
      return;
    }

    if (!filteredParents.some((parent) => String(parent.id) === String(candidateParentId))) {
      setCandidateParentId(String(filteredParents[0].id));
    }
  }, [candidateParentId, filteredParents]);

  const selectedParents = useMemo(
    () => selectedParentIds
      .map((parentId) => parents.find((parent) => String(parent.id) === String(parentId)))
      .filter(Boolean),
    [parents, selectedParentIds]
  );

  useEffect(() => {
    setSearch('');
    setCandidateParentId('');
    setSelectedParentIds([]);
    setSaving(false);
    setError('');
  }, [student?.id]);

  if (!student) return null;

  const bestMatch = filteredParents[0] || null;
  const addSelectedParent = () => {
    if (!candidateParentId) return;
    setSelectedParentIds((current) => (
      current.includes(candidateParentId) ? current : [...current, candidateParentId]
    ));
  };

  const removeSelectedParent = (parentId) => {
    setSelectedParentIds((current) => current.filter((item) => item !== parentId));
  };

  const handleAssign = async () => {
    if (!selectedParentIds.length) return;

    setSaving(true);
    setError('');
    try {
      await onAssign?.(selectedParentIds.map(Number));
    } catch (err) {
      setError(err.message || 'Failed to assign parent or guardian records');
      setSaving(false);
    }
  };

  return (
    <div className="sd-support-overlay" onClick={onClose}>
      <div className="sd-student-parent-modal" onClick={(event) => event.stopPropagation()}>
        <div className="sd-student-parent-modal-header">
          <button type="button" className="sd-support-back" onClick={onClose} aria-label="Back">
            ←
          </button>

          <div className="sd-student-parent-modal-copy">
            <h2>Match Parent Or Guardian Records</h2>
            <p>Assign one or more parent or guardian records to this student.</p>
          </div>
        </div>

        <div className="sd-student-parent-student-card">
          <SchoolAvatar record={student} className="sd-student-parent-avatar" />
          <div className="sd-student-parent-student-copy">
            <div className="sd-student-parent-student-name">{student.f_name} {student.l_name}</div>
            <div className="sd-student-parent-student-meta">{getGradeLabel(student.grade, gradeLabelMap)}</div>
          </div>
        </div>

        {reviewMatchesLoading ? (
          <div className="sd-student-parent-review-note muted">
            Checking queued import records...
          </div>
        ) : null}

        {!reviewMatchesLoading && reviewMatches.length ? (
          <div className="sd-student-parent-review-note warning">
            <div>
              <strong>{reviewMatches.length} queued parent or guardian record{reviewMatches.length === 1 ? '' : 's'} may match this student.</strong>
              <span>Review queued imports before assigning an existing live record.</span>
            </div>

            <div className="sd-student-parent-review-list">
              {reviewMatches.slice(0, 3).map((row) => (
                <div key={row.id} className="sd-student-parent-review-row">
                  <span>{getReviewRowTitle(row)}</span>
                  <small>
                    {[getReviewIssueText(row), row.match_reasons?.join(', ')]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </div>
              ))}
            </div>

            {reviewMatches.length > 3 ? (
              <small className="sd-student-parent-review-more">
                +{reviewMatches.length - 3} more queued match{reviewMatches.length - 3 === 1 ? '' : 'es'}
              </small>
            ) : null}

            {onOpenReviewQueue ? (
              <button type="button" className="sd-student-parent-review-action" onClick={onOpenReviewQueue}>
                Open Review Queue
              </button>
            ) : null}
          </div>
        ) : null}

        {!reviewMatchesLoading && reviewMatchesError ? (
          <div className="sd-student-parent-review-note muted">
            Import review queue could not be checked.
          </div>
        ) : null}

        <div className="sd-student-parent-form-card">
          <label className="sd-student-parent-label" htmlFor="student-parent-search">Search all parents and guardians</label>
          <input
            id="student-parent-search"
            className="sd-student-parent-input"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by parent/guardian name, email, or phone"
          />

          {bestMatch ? (
            <div className="sd-student-parent-best-match">
              Best match: <strong>{formatContactName(bestMatch)}</strong>
            </div>
          ) : (
            <div className="sd-student-parent-best-match">No matching parents found.</div>
          )}

          <div className="sd-student-parent-select-row">
            <select
              className="sd-student-parent-select"
              value={candidateParentId}
              onChange={(event) => setCandidateParentId(event.target.value)}
              disabled={!filteredParents.length}
            >
              {filteredParents.length ? filteredParents.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {formatContactName(parent)}
                  {` • ${getContactTypeLabel(parent)}`}
                  {normalize(parent.l_name) === normalize(student.l_name) ? ' • surname match' : ''}
                  {!hasLinkedChildren(parent)
                    ? ' • no linked children'
                    : ` • ${getLinkedChildrenCount(parent)} linked child${getLinkedChildrenCount(parent) === 1 ? '' : 'ren'}`}
                </option>
              )) : (
                <option value="">No parents available</option>
              )}
            </select>

            <button
              type="button"
              className="sd-student-parent-add"
              onClick={addSelectedParent}
              disabled={!candidateParentId}
            >
              Add Parent
            </button>
          </div>

          <div className="sd-student-parent-selected">
            {selectedParents.length ? selectedParents.map((parent) => (
              <div key={parent.id} className="sd-student-parent-pill">
                <span>{formatContactName(parent)} • {getContactTypeLabel(parent)}</span>
                <button type="button" onClick={() => removeSelectedParent(String(parent.id))} aria-label={`Remove ${formatContactName(parent)}`}>
                  ×
                </button>
              </div>
            )) : (
              <div className="sd-student-parent-empty">No parent or guardian records selected yet.</div>
            )}
          </div>

          {error ? <div className="sa-error">{error}</div> : null}

          <button
            type="button"
            className="sd-support-send"
            onClick={handleAssign}
            disabled={saving || !selectedParentIds.length}
          >
            {saving ? 'Assigning…' : 'Assign Records'}
          </button>
        </div>
      </div>
    </div>
  );
}
